'use client'

import { memo, useCallback } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useNinxl8BitThemeMode } from '@/app/(marketing)/_components/8bit'
import { NINK_DOWNLOAD_HREF } from './nink-links'

type NavLinkItem = { label: string; href: string; kind: 'link' }
type NavScrollItem = { label: string; id: string; kind: 'scroll' }
type NavItem = NavLinkItem | NavScrollItem

const NAV_ITEMS: NavItem[] = [
  { label: 'NIN', href: '/nin', kind: 'link' },
  { label: 'NINX', href: '/ninxl', kind: 'link' },
  { label: 'Ink Flow', id: 'inkflow', kind: 'scroll' },
  { label: 'Apps', id: 'works-in', kind: 'scroll' },
  { label: 'Privacy', id: 'privacy', kind: 'scroll' },
  { label: 'Docs', href: '/docs/open-source/nink', kind: 'link' },
  { label: 'Contact', id: 'contact', kind: 'scroll' },
]

export const Nink8BitNav = memo(function Nink8BitNav({
  onScrollTo,
}: {
  onScrollTo?: (id: string) => void
}) {
  const pathname = usePathname() ?? ''
  const isLanding = pathname === '/nink'
  const theme = useNinxl8BitThemeMode()
  const logoSrc = theme === 'dark' ? '/nink_logo_dark.svg' : '/nink_logo_light.svg'

  const scrollTo = useCallback(
    (id: string) => {
      if (onScrollTo) {
        onScrollTo(id)
        return
      }
      const target =
        document.getElementById(id) ??
        document.querySelector<HTMLElement>(`[data-section="${id}"]`)
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    [onScrollTo],
  )

  return (
    <nav className="ninxl-8bit-nav sticky top-0 z-50" aria-label="NINK">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-8">
        {/* baby: Same logo + brand scale as /nin and /ninxl nav */}
        <Link href="/nink" className="flex items-center gap-3">
          <Image
            key={theme}
            src={logoSrc}
            alt=""
            width={32}
            height={32}
            className="h-8 w-8"
            aria-hidden
            priority
          />
          <span className="nink-hybrid-brand ninxl-theme-text">NINK</span>
        </Link>

        <div className="hidden items-center gap-8 md:flex">
          {NAV_ITEMS.map((item) => {
            if (item.kind === 'link') {
              const isActive =
                pathname === item.href || pathname.startsWith(`${item.href}/`)
              return (
                <Link
                  key={item.label}
                  href={item.href}
                  className={`nink-hybrid-nav-link${isActive ? ' ninxl-theme-text-accent' : ''}`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              )
            }

            if (isLanding) {
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => scrollTo(item.id)}
                  className="nink-hybrid-nav-link border-0 bg-transparent cursor-pointer"
                >
                  {item.label}
                </button>
              )
            }

            return (
              <Link
                key={item.label}
                href={`/nink#${item.id}`}
                className="nink-hybrid-nav-link"
              >
                {item.label}
              </Link>
            )
          })}
        </div>

        {isLanding ? (
          <button
            type="button"
            onClick={() => scrollTo('download')}
            className="nink-hybrid-btn nink-hybrid-btn-primary nink-hybrid-btn-nav"
          >
            Download
          </button>
        ) : (
          <Link
            href={NINK_DOWNLOAD_HREF}
            className="nink-hybrid-btn nink-hybrid-btn-primary nink-hybrid-btn-nav"
          >
            Download
          </Link>
        )}
      </div>
    </nav>
  )
})
